import {createSelector} from 'reselect';

import {getEventTime, getEventDate} from '@stubhub/react-event-helper';

import {getSeatTicketTraits} from '../../../../helpers/get-seat-traits/';
import {sortArrayObjects} from '../../../../helpers/sortArrayObjects';
import FEATURE_FLAGS from '../../../../utils/featureFlags';

import {IDS_LABELS_MAPPING, KEY_INSTANT} from './deliveryType-ids';

/*
 *Average price from listings
 */
export const averagePriceDataSelector = createSelector(
  (state = {}) => state.listings && state.listings.pricingSummary,
  (state = {}) => state.listings && state.listings.currency,
  (pricingSummary = {}, currency) => {
    const {averageTicketPriceWithCurrency = {}} = pricingSummary;

    return {
      averageTicketPrice: averageTicketPriceWithCurrency.amount,
      averageTicketPriceCurrency: averageTicketPriceWithCurrency.currency || currency,
    };
  }
);

/*
 * Props for ticket quantity
 */
export const listingAvailableQtySelector = createSelector(
  (state = {}) => state.listings,
  (listings = {}) => {
    const {currency = 'USD', availableQuantities = []} = listings;

    const displayedQuantities = [];

    for (let i = 0; i < availableQuantities.length; i++) {
      const currentQty = availableQuantities[i];
      if (currentQty < 5) {
        displayedQuantities.push(currentQty);
      } else {
        displayedQuantities.push(5);
        break;
      }
    }

    return {
      availableQuantities: listings.availableQuantities,
      displayedQuantities,
      totalTickets: listings.totalTickets,
      totalListings: listings.totalListings,
      currency,
      listingLoading: listings.listingLoading || false,
      loading: listings.loading,
    };
  }
);

/*
 * Props for sort by section
 */
export const eventSortBySelector = createSelector(
  (state) => state.event,
  (event = {}) => {
    return {
      venueConfig: event.venueConfig,
      showTicketRecoInd: event.showTicketRecoInd,
    };
  }
);

/*
 *SeatTraits for features section
 */
export const eventStatsSelector = createSelector(
  (state) => state.listings,
  (listings = {}) => {
    return {
      seatTraits: getSeatTicketTraits(listings.eventStats),
    };
  }
);

export const isLoading = createSelector(
  (state) => state.event,
  (event = {}) => event.loading
);

export const deviceSelector = (state) => state.device;

export const getDeliveryType = createSelector(
  (localState) => localState?.fulfillmentWindows,
  (fulfillmentWindows = []) => {
    const deliveries = removeDuplicates(fulfillmentWindows, 'deliveryMethodDisplayName').map(
      (fulfillmentWindow = {}) => {
        const {deliveryMethodDisplayName, deliveryMethod = {}} = fulfillmentWindow;

        return {
          name: deliveryMethodDisplayName,
          displayName: deliveryMethodDisplayName,
          id: deliveryMethod.deliveryTypeId,
        };
      }
    );

    return {
      deliveryTypes: deliveries && deliveries.sort(sortArrayObjects('displayName')),
    };
  }
);

export const availableTicketTypesSelector = createSelector(getDeliveryType, ({deliveryTypes = []} = {}) => {
  const deliveryTypeIds = deliveryTypes.map(({id} = {}) => id);

  return [...new Set(deliveryTypeIds.map((id) => IDS_LABELS_MAPPING[id]).flat())];
});

export const isInstantTicketAvailableSelector = createSelector(
  availableTicketTypesSelector,
  (availableTicketTypes = []) => availableTicketTypes.includes(KEY_INSTANT)
);

export const getLocale = createSelector(
  (state = {}) => state.gsConfig,
  (gsConfig = {}) => gsConfig.currentLocale
);

export const getPriceIncludeFeesPreselected = createSelector(
  (state = {}) => state.gsConfig,
  (gsConfig = {}) => {
    return gsConfig.features && gsConfig.features['event.legal.priceIncludeFees.select'];
  }
);

/*
 *Filter footer data
 */
export const filterFooterDataSelector = createSelector(
  (state) => state.event,
  (event) => {
    if (!event) {
      return {};
    }

    const {eventDateLocal, displayAttributes, name: eventName, isGA} = event;
    const eventTime = getEventTime({eventDateLocal, displayAttributes});
    const eventDate = getEventDate({eventDateLocal, displayAttributes});

    return {
      eventName,
      eventTime,
      eventDate,
      isGA,
    };
  }
);

/*
 * Props for price slider
 */
export const listingDataSelector = createSelector(
  (state) => state.listings,
  (listings = {}) => {
    const {eventPricingSummary, pricingSummary = {}} = listings;
    const source =
      eventPricingSummary && Object.keys(eventPricingSummary).length ? eventPricingSummary : pricingSummary;
    const {minTicketPrice, percentiles = []} = source;
    const [maxPercentile = {}] = percentiles;
    const minPrice = Math.floor(minTicketPrice);
    const maxPrice = Math.ceil(maxPercentile.value);

    return {minPrice, maxPrice, eventPricingSummary: source};
  }
);

export const getShowPricingFilter = (minPrice, maxPrice) => {
  return createSelector(
    (state) => state.event,
    (event = {}) => {
      const {isGA} = event;
      const validMinPrice = isNumberValidForFilter(minPrice);
      const validMaxPrice = isNumberValidForFilter(maxPrice);

      return !isGA && validMinPrice && validMaxPrice;
    }
  );
};

export const getShowInstantDeliveryFilter = createSelector(
  (state = {}) => state?.gsConfig,
  ({features = {}} = {}) => !!features[FEATURE_FLAGS.event.showInstantDeliveryFilter]
);

export const getTotalListings = createSelector(
  (state = {}) => state.listings,
  (listings = {}) => {
    if (listings.totalListings) {
      const parsed = parseInt(listings.totalListings, 10);
      if (!Number.isNaN(parsed)) {
        return parsed;
      }
    }

    return 0;
  }
);

const removeDuplicates = (fulfillmentWindows = [], propertyName) => {
  return fulfillmentWindows.filter((obj, pos, arr) => {
    return arr.map((mapObj) => mapObj[propertyName]).indexOf(obj[propertyName]) === pos;
  });
};

const isNumberValidForFilter = (numberFilter) => {
  return !!numberFilter && Object.is(typeof numberFilter, 'number') && numberFilter >= 0 && !Number.isNaN(numberFilter);
};
